import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import {
    LiteTradePortfolioListItemWrapperStyled,
    LiteTradePortfolioListStyled,
    LiteTradePortfolioStyled,
    LiteTradePortfolioSumPlStyled,
    LiteTradePortfolioTitleStyled
} from "./styled/LiteTradePortfolioStyled.js";
import { getLiteTradeAccountPortfolio, getLiteTradeAssets, getLiteTradeMarketData } from "../../liteTradeSelectors.js";
import _ from "lodash";
import Asset from "../../../Asset/Asset.js";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import { openMobileSliderPageAction } from "../../../../mobileAppCore/mobileAppActions.js";
import { Alert } from "react-bootstrap";
import SlideMobilePositionModal
    from "../../../../mobileAppCore/components/MobileSliderPages/slides/SlideMobilePositionModal.jsx";
import { getUserSelectedAccountCode, getUserSelectedAccountType } from "../../../../app/appSelectors.js";
import LiteTradePortfolioPositionRow from "./LiteTradePortfolioPositionRow.jsx";
import LiteTradePortfolioOrderRow from "./LiteTradePortfolioOrderRow.jsx";
import SlideMobileOrderModal
    from "../../../../mobileAppCore/components/MobileSliderPages/slides/SlideMobileOrderModal.jsx";
import { logAppsflyerAction } from "../../../../app/appActions";
import { LOG_AF_EVENT_NAMES } from "../../../../app/appConstants";
import { addMarketDataWatchAction, removeMarketDataWatchAction } from "../../liteTradeActions.js";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import LiteTradePortfolioEmpty from "./LiteTradePortfolioEmpty.jsx";
import {
    LITE_TRADE_ORDER_STATUSES,
    LITE_TRADE_POSITION_EFFECTS,
    LITE_TRADE_POSITION_SIDE
} from "../../liteTradeConstants";
import amountFormat from "../../../../utils/amountFormat.js";
import withDebounced from "../../../../hooks/withDebounced.js";

function LiteTradePortfolio(props) {
    const {
        assets,
        logAppsflyer,
        portfolio,
        selectedAccountCode,
        selectedAccountType,
        symbol,
        addMarketDataWatch,
        removeMarketDataWatch,
        marketData,
    } = props;

    const { getLangPath } = useLangContext();
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [positions, setPositions] = useState([]);
    const [orders, setOrders] = useState([]);

    const hasPositions = useMemo(() => {
        return positions.length > 0;
    }, [positions]);

    const hasOrders = useMemo(() => {
        return orders.length > 0;
    }, [orders]);

    const extendPortfolioItem = useCallback((item, type) => {
        const asset = _.find(assets, { symbol: item[type === 'position' ? 'symbol' : 'instrument'] });
        if (!asset) return {};
        const assetInstance = new Asset(asset, {
            assets,
            selectedAccountCode,
        });
        return {
            ...item,
            assetInstance,
        };
    }, [assets, selectedAccountCode]);

    const addLinksToPosition = useCallback((item) => {
        return {
            ...item,
            links: portfolio.orders.filter(order =>
                order.legs[0].positionCode === item.positionCode &&
                order.status === LITE_TRADE_ORDER_STATUSES.WORKING
            ),
        }
    }, [portfolio.orders]);

    const addLinksToOrder = useCallback((item) => {
        if (!item.links) {
            return item;
        }
        return {
            ...item,
            links: item.links.map(link => (portfolio.orders.find(order => order.orderCode === link.linkedOrder))),
        }
    }, [portfolio.orders]);

    useEffect(() => {
        positions.forEach(item => {
            if (!symbol || symbol === item.symbol) {
                addMarketDataWatch({ fromComponentName: 'portfolio', symbol: item.symbol });
            }
        });
        orders.forEach(item => {
            if (!symbol || symbol === item.instrument) {
                addMarketDataWatch({ fromComponentName: 'portfolio', symbol: item.instrument });
            }
        });
        return () => {
            removeMarketDataWatch({ fromComponentName: 'portfolio' });
        }
    }, [addMarketDataWatch, orders, positions, removeMarketDataWatch, symbol]);

    useEffect(() => {
        if (assets.length) {
            if (portfolio?.positions) {
                setPositions(portfolio.positions
                    .filter(item => !symbol || item.symbol === symbol)
                    .filter(item => item.quantity > 0)
                    .map(item => extendPortfolioItem(item, 'position'))
                    .map(addLinksToPosition));
                setOrders(portfolio.orders
                    .filter(item => !symbol || item.instrument === symbol)
                    .filter(item => item.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.OPEN)
                    .filter(item => item.status === 'WORKING')
                    .map(item => extendPortfolioItem(item, 'order'))
                    .map(addLinksToOrder));
            }
        }
    }, [assets, portfolio, symbol, extendPortfolioItem, addLinksToOrder, addLinksToPosition]);

    const handleSelectPosition = useCallback((e) => {
        setSelectedPosition(e.currentTarget.dataset.id)
        const selectedPosition = _.find(positions, { positionCode: e.currentTarget.dataset.id });
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_POSITION,
            payload: {
                account_type: selectedAccountType,
                asset: selectedPosition.symbol,
                position_code: selectedPosition.positionCode,
            }
        });
    }, [logAppsflyer, positions, selectedAccountType]);
    const handleSelectOrder = useCallback((e) => {
        setSelectedOrder(e.currentTarget.dataset.id);
        const selectedOrder = _.find(orders, { orderId: parseInt(e.currentTarget.dataset.id) });
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_ORDER,
            payload: {
                account_type: selectedAccountType,
                asset: selectedOrder.instrument,
                order_code: selectedOrder.orderCode,
                order_type: selectedOrder.type,
            }
        });
    }, [logAppsflyer, orders, selectedAccountType]);

    const selectedPositionInfo = useMemo(() =>
            _.find(positions, { positionCode: selectedPosition }),
        [positions, selectedPosition]);

    const selectedOrderInfo = useMemo(() =>
            _.find(orders, { orderId: parseInt(selectedOrder) }),
        [orders, selectedOrder]);

    const sumPl = useMemo(() => {
        let ready = true;
        const amount = positions.reduce((acc, cur) => {
            const side = cur.side === LITE_TRADE_POSITION_SIDE.BUY ? 'bid' : 'ask';
            if (!marketData?.[cur.symbol]) ready = false;
            const pl = cur.assetInstance.getPositionPL(cur, marketData?.[cur.symbol]?.[side] ?? 0)
            return acc + pl;
        }, 0);

        return !!ready && <span
            style={{ color: amount > 0 ? 'var(--color-green)' : 'var(--color-red)' }}>{amountFormat(amount, 2, '$')}</span>;
    }, [marketData, positions]);

    const handleCloseMobilePositionModal = useCallback(() => setSelectedPosition(null), []);
    const handleCloseMobileOrderModal = useCallback(() => setSelectedOrder(null), []);

    if (!portfolio?.positions) {
        return <Loader show={true}/>;
    }

    return <LiteTradePortfolioStyled>
        {
            !!selectedPositionInfo && <SlideMobilePositionModal
                accountCode={selectedAccountCode}
                assetInstance={selectedPositionInfo.assetInstance}
                onClose={handleCloseMobilePositionModal}
                positionCode={selectedPosition}
                symbol={selectedPositionInfo.symbol}
                openAssetOnClick={!symbol}
            />
        }
        {
            !!selectedOrderInfo && <SlideMobileOrderModal
                assetInstance={selectedOrderInfo.assetInstance}
                orderId={selectedOrder}
                onClose={handleCloseMobileOrderModal}
                symbol={selectedOrderInfo.instrument}
                openAssetOnClick={!symbol}
            />
        }
        {
            !hasOrders && !hasPositions && <>
                {
                    !!symbol ?
                        <Alert variant={'info-outside'}>{getLangPath('lite_version.order.no_positions_yet')}</Alert>
                        : <LiteTradePortfolioEmpty/>
                }
            </>
        }
        {
            hasPositions && <>
                <LiteTradePortfolioTitleStyled>
                    <h6>{getLangPath('lite_version.home.positions')}</h6>
                    <LiteTradePortfolioSumPlStyled>{sumPl}</LiteTradePortfolioSumPlStyled>
                </LiteTradePortfolioTitleStyled>
                <LiteTradePortfolioListStyled>
                    {
                        positions.map(item => <Fragment key={item.positionCode}>
                            <LiteTradePortfolioListItemWrapperStyled
                                data-id={item.positionCode}
                                onClick={handleSelectPosition}>
                                <LiteTradePortfolioPositionRow item={item} symbol={item.symbol}
                                                               from={symbol ? 'assetInfo' : 'portfolio'}/>
                            </LiteTradePortfolioListItemWrapperStyled>
                        </Fragment>)
                    }
                </LiteTradePortfolioListStyled>
            </>
        }
        {
            hasOrders && <>
                <LiteTradePortfolioTitleStyled>
                    <h6>{getLangPath('lite_version.home.orders')}</h6>
                </LiteTradePortfolioTitleStyled>
                <LiteTradePortfolioListStyled>
                    {
                        orders.map(item => <Fragment key={item.orderCode}>
                            <LiteTradePortfolioListItemWrapperStyled
                                data-id={item.orderId}
                                onClick={handleSelectOrder}>
                                <LiteTradePortfolioOrderRow item={item}
                                                            from={symbol ? 'assetInfo' : 'portfolio'}/>
                            </LiteTradePortfolioListItemWrapperStyled>
                        </Fragment>)
                    }
                </LiteTradePortfolioListStyled>
            </>
        }
    </LiteTradePortfolioStyled>
}

const mapStateToProps = createStructuredSelector({
    assets: getLiteTradeAssets,
    portfolio: getLiteTradeAccountPortfolio,
    selectedAccountCode: getUserSelectedAccountCode,
    selectedAccountType: getUserSelectedAccountType,
    marketData: getLiteTradeMarketData,
});

const mapDispatchToProps = (dispatch) => ({
    logAppsflyer: payload => dispatch(logAppsflyerAction(payload)),
    openMobileSliderPage: payload => dispatch(openMobileSliderPageAction(payload)),
    addMarketDataWatch: payload => dispatch(addMarketDataWatchAction(payload)),
    removeMarketDataWatch: payload => dispatch(removeMarketDataWatchAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withDebounced({
    propNames: ['marketData'],
    debounce: 1000
})(memo(LiteTradePortfolio)));
